import type {ReactElement} from 'react';
import {useContext, useEffect, useRef} from 'react';
import type {Component} from 'grapesjs';
import {Modal} from '@Components/modal';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Panel, PanelHeader} from '@Components/panel';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {getRightPanelSector} from '@FormMakerReact/layout/form-maker-right-panel/right-panel-registry';
import {getComponentType} from '@FormMakerReact/lib/form-maker-canvas-library';
import {getTileEntries} from '@FormMakerReact/lib/form-maker-tile-meta-library';
import type {TileEntry} from '@FormMakerReact/lib/form-maker-tile-meta-library.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {MobileControlPanelProps, MountedSheetProps} from './form-maker-mobile-control-sheet.types';
import styles from './form-maker-mobile-control-sheet.module.scss';

const PANEL_ID = 'form-maker-mobile-control-panel';

export function FormMakerMobileControlSheet(): ReactElement | null {
  const {state} = useContext(formMakerContext);
  const modalData = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.FORM_MAKER_MOBILE_CONTROL_MODAL]);
  if (!modalData?.isOpen) {
    return null;
  }
  const {labelKey, isFullHeight} = modalData.panelProps as MobileControlPanelProps;
  const selected = state.selectedComponent;
  if (!selected) {
    return null;
  }
  const entry = resolveTileEntry(selected, labelKey);
  if (!entry) {
    return null;
  }
  return <MountedSheet entry={entry} selected={selected} isFullHeight={isFullHeight} />;
}

function MountedSheet({entry, selected, isFullHeight}: MountedSheetProps): ReactElement {
  const dispatch = useAppDispatch();
  const {editor} = useContext(formMakerContext);
  useDefaultPanelClose(PANEL_ID);

  // Sheet overlay is non-blocking, so a canvas tap that lands on any other component — or that
  // simply deselects — is the tap-outside gesture. Re-selecting the same component is a no-op.
  const initialSelectedCidRef = useRef<string>(selected.cid);

  useEffect((): (() => void) | undefined => {
    if (!editor) {
      return undefined;
    }
    const close = (): void => {
      dispatch(closeModal(MODALS_IDS.FORM_MAKER_MOBILE_CONTROL_MODAL));
    };
    const onSelected = (component: Component): void => {
      if (component.cid === initialSelectedCidRef.current) {
        return;
      }
      close();
    };
    editor.on('component:selected', onSelected);
    editor.on('component:deselected', close);
    return (): void => {
      editor.off('component:selected', onSelected);
      editor.off('component:deselected', close);
    };
  }, [editor, dispatch]);

  const onClose = (): void => {
    dispatch(closeModal(MODALS_IDS.FORM_MAKER_MOBILE_CONTROL_MODAL));
  };
  return (
    <Modal
      modalId={MODALS_IDS.FORM_MAKER_MOBILE_CONTROL_MODAL}
      showNonFunctionalDragHandle
      modalMaxHeight={isFullHeight ? '90vh' : 'fit-content'}
      modalMaxWidth="100%"
      modalWidth="100%"
      panelId={PANEL_ID}
      stickToBottom
      isBottomSheetOnMobile
      isBottomSheetNonBlocking
      sizeToContent
    >
      <Panel
        panelHeader={<PanelHeader title={window.i18next.t(entry.meta.labelKey)} onClose={onClose} />}
        panelContent={<div className={styles.body}>{renderTileControl(entry, selected)}</div>}
      />
    </Modal>
  );
}

function resolveTileEntry(selected: Component, labelKey: string): TileEntry | undefined {
  const sector = getRightPanelSector(getComponentType(selected));
  if (!sector) {
    return undefined;
  }
  return getTileEntries(sector.controls, selected).find((entry) => entry.meta.labelKey === labelKey);
}

function renderTileControl({Control}: TileEntry, component: Component): ReactElement {
  return <Control component={component} hideSectionHeading />;
}
